import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { appointmentService } from '../services/AppointmentService';
import { clientService } from '../services/ClientService';
import { userService } from '../services/UserService';
import { Appointment } from '../models/Appointment';
import { Client } from '../models/Client';
import { User } from '../models/User';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  User as UserIcon,
  X,
  Video,
} from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
} from 'date-fns';

type ViewMode = '1-month' | '3-month';

interface DayAppointments {
  date: Date;
  appointments: Appointment[];
  isCurrentMonth: boolean;
}

export const Calendar: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { company } = useCompany();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('1-month');
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [showDayModal, setShowDayModal] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, currentUser]);

  const loadData = async () => {
    if (!company || !currentUser) {
      setLoading(false);
      return;
    }

    try {
      const [companyClients, companyUsers] = await Promise.all([
        clientService.getClientsByCompany(company.id),
        userService.getUsersByCompany(company.id),
      ]);
      setClients(companyClients);
      setUsers(companyUsers);

      let userAppointments: Appointment[] = [];
      if (currentUser.role === 'rbt') {
        userAppointments = await appointmentService.getAppointmentsByRbt(currentUser.id);
      } else if (currentUser.role === 'supervisor') {
        userAppointments = await appointmentService.getAppointmentsBySupervisor(currentUser.id);
      } else if (currentUser.role === 'admin') {
        userAppointments = await appointmentService.getAppointmentsByCompany(company.id);
      }

      // Auto-mark past-due, never-started appointments as no-show.
      const reconciled = await appointmentService.reconcileMissedAppointments(userAppointments);
      setAppointments(reconciled);
      setError(null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    return client ? `${client.firstName} ${client.lastName}` : 'Unknown Client';
  };

  const getRbtName = (rbtId: string) => {
    const rbt = users.find((u) => u.id === rbtId);
    return rbt ? rbt.name : 'Unknown RBT';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return '#4F6367';
      case 'checked-in':
        return '#F59E0B';
      case 'in-progress':
        return '#10B981';
      case 'completed':
        return '#7A9E9F';
      case 'cancelled':
        return '#EF4444';
      case 'no-show':
        return '#EF4444';
      default:
        return '#6b6b6b';
    }
  };

  // Generate calendar days for a single month
  const generateMonthDays = (monthDate: Date): DayAppointments[] => {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });

    const days: DayAppointments[] = [];
    let day = startDate;

    while (day <= endDate) {
      const currentDay = day; // capture per-iteration so the filter closure doesn't reference the mutating loop var
      const dayAppointments = appointments.filter((apt) =>
        isSameDay(apt.startTime, currentDay)
      );
      days.push({
        date: currentDay,
        appointments: dayAppointments,
        isCurrentMonth: isSameMonth(currentDay, monthDate),
      });
      day = addDays(day, 1);
    }

    return days;
  };

  // Generate months to display based on view mode
  const monthsToDisplay = useMemo(() => {
    if (viewMode === '1-month') {
      return [currentDate];
    } else {
      return [
        currentDate,
        addMonths(currentDate, 1),
        addMonths(currentDate, 2),
      ];
    }
  }, [currentDate, viewMode]);

  const handlePrevious = () => {
    if (viewMode === '1-month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(subMonths(currentDate, 3));
    }
  };

  const handleNext = () => {
    if (viewMode === '1-month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 3));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDayClick = (day: DayAppointments) => {
    setSelectedDay(day.date);
    setShowDayModal(true);
  };

  const selectedDayAppointments = useMemo(() => {
    if (!selectedDay) return [];
    return appointments
      .filter((apt) => isSameDay(apt.startTime, selectedDay))
      .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
  }, [selectedDay, appointments]);

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '2rem', textAlign: 'center' }}>
        <div
          style={{
            display: 'inline-block',
            width: '48px',
            height: '48px',
            border: '4px solid #e5e0db',
            borderTop: '4px solid #4F6367',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <p style={{ marginTop: '1rem', color: '#6b6b6b' }}>Loading calendar...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <>
      <div className="container" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
            flexWrap: 'wrap',
            gap: '1rem',
          }}
        >
          <div>
            <h1 style={{ marginBottom: '0.5rem' }}>Calendar</h1>
            <p style={{ color: '#6b6b6b' }}>
              {viewMode === '1-month'
                ? format(currentDate, 'MMMM yyyy')
                : `${format(currentDate, 'MMMM yyyy')} - ${format(addMonths(currentDate, 2), 'MMMM yyyy')}`}
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* View Mode Toggle */}
            <div
              style={{
                display: 'flex',
                border: '1px solid #D1D5DB',
                borderRadius: '0.5rem',
                overflow: 'hidden',
              }}
            >
              <button
                onClick={() => setViewMode('1-month')}
                style={{
                  padding: '0.5rem 1rem',
                  background: viewMode === '1-month' ? '#4F6367' : 'white',
                  color: viewMode === '1-month' ? 'white' : '#374151',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                1 Month
              </button>
              <button
                onClick={() => setViewMode('3-month')}
                style={{
                  padding: '0.5rem 1rem',
                  background: viewMode === '3-month' ? '#4F6367' : 'white',
                  color: viewMode === '3-month' ? 'white' : '#374151',
                  border: 'none',
                  borderLeft: '1px solid #D1D5DB',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                3 Months
              </button>
            </div>

            {/* Navigation Controls */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button
                onClick={handlePrevious}
                style={{
                  padding: '0.5rem',
                  background: 'white',
                  border: '1px solid #D1D5DB',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Previous"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={handleToday}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'white',
                  border: '1px solid #D1D5DB',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                Today
              </button>
              <button
                onClick={handleNext}
                style={{
                  padding: '0.5rem',
                  background: 'white',
                  border: '1px solid #D1D5DB',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Next"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {/* Calendar Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: viewMode === '1-month' ? '1fr' : 'repeat(3, 1fr)',
            gap: '1.5rem',
          }}
        >
          {monthsToDisplay.map((monthDate, monthIndex) => {
            const monthDays = generateMonthDays(monthDate);

            return (
              <div
                key={monthIndex}
                className="card"
                style={{
                  padding: viewMode === '3-month' ? '1rem' : '1.5rem',
                }}
              >
                {/* Month Header */}
                <h3
                  style={{
                    textAlign: 'center',
                    marginBottom: '1rem',
                    color: '#374151',
                    fontSize: viewMode === '3-month' ? '1rem' : '1.25rem',
                  }}
                >
                  {format(monthDate, 'MMMM yyyy')}
                </h3>

                {/* Week Day Headers */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    marginBottom: '0.5rem',
                  }}
                >
                  {weekDays.map((day) => (
                    <div
                      key={day}
                      style={{
                        textAlign: 'center',
                        padding: '0.5rem',
                        fontSize: viewMode === '3-month' ? '0.65rem' : '0.75rem',
                        fontWeight: 600,
                        color: '#6b6b6b',
                        textTransform: 'uppercase',
                      }}
                    >
                      {viewMode === '3-month' ? day.charAt(0) : day}
                    </div>
                  ))}
                </div>

                {/* Days Grid */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '2px',
                  }}
                >
                  {monthDays.map((day, dayIndex) => {
                    const hasAppointments = day.appointments.length > 0;
                    const isTodayDate = isToday(day.date);

                    return (
                      <div
                        key={dayIndex}
                        onClick={() => hasAppointments && handleDayClick(day)}
                        style={{
                          minHeight: viewMode === '3-month' ? '40px' : '80px',
                          padding: viewMode === '3-month' ? '0.25rem' : '0.5rem',
                          backgroundColor: isTodayDate
                            ? '#B8D8D8'
                            : day.isCurrentMonth
                            ? '#FFFFFF'
                            : '#F9FAFB',
                          border: isTodayDate ? '2px solid #4F6367' : '1px solid #e5e0db',
                          borderRadius: '0.375rem',
                          cursor: hasAppointments ? 'pointer' : 'default',
                          transition: 'all 0.15s ease',
                          position: 'relative',
                        }}
                        onMouseEnter={(e) => {
                          if (hasAppointments) {
                            e.currentTarget.style.backgroundColor = '#EDEAE4';
                            e.currentTarget.style.transform = 'scale(1.02)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isTodayDate
                            ? '#B8D8D8'
                            : day.isCurrentMonth
                            ? '#FFFFFF'
                            : '#F9FAFB';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        {/* Day Number */}
                        <div
                          style={{
                            fontSize: viewMode === '3-month' ? '0.75rem' : '0.875rem',
                            fontWeight: isTodayDate ? 700 : 500,
                            color: day.isCurrentMonth ? '#374151' : '#9CA3AF',
                            marginBottom: '0.25rem',
                          }}
                        >
                          {format(day.date, 'd')}
                        </div>

                        {/* Appointment Indicators */}
                        {hasAppointments && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {viewMode === '3-month' ? (
                              // Compact dots for 3-month view
                              <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap' }}>
                                {day.appointments.slice(0, 3).map((apt, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      width: '6px',
                                      height: '6px',
                                      borderRadius: '50%',
                                      backgroundColor: getStatusColor(apt.status),
                                    }}
                                  />
                                ))}
                                {day.appointments.length > 3 && (
                                  <span
                                    style={{
                                      fontSize: '0.6rem',
                                      color: '#6b6b6b',
                                    }}
                                  >
                                    +{day.appointments.length - 3}
                                  </span>
                                )}
                              </div>
                            ) : (
                              // Detailed view for 1-month
                              <>
                                {day.appointments.slice(0, 2).map((apt, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      fontSize: '0.65rem',
                                      padding: '2px 4px',
                                      backgroundColor: getStatusColor(apt.status),
                                      color: 'white',
                                      borderRadius: '0.25rem',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {format(apt.startTime, 'h:mma')} {getClientName(apt.clientId).split(' ')[0]}
                                  </div>
                                ))}
                                {day.appointments.length > 2 && (
                                  <div
                                    style={{
                                      fontSize: '0.65rem',
                                      color: '#4F6367',
                                      fontWeight: 600,
                                    }}
                                  >
                                    +{day.appointments.length - 2} more
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div
          className="card"
          style={{
            marginTop: '1.5rem',
            padding: '1rem',
            display: 'flex',
            gap: '1.5rem',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>Legend:</span>
          {[
            { status: 'scheduled', label: 'Scheduled' },
            { status: 'checked-in', label: 'Checked In' },
            { status: 'in-progress', label: 'In Progress' },
            { status: 'completed', label: 'Completed' },
            { status: 'cancelled', label: 'Cancelled' },
          ].map((item) => (
            <div key={item.status} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: getStatusColor(item.status),
                }}
              />
              <span style={{ fontSize: '0.75rem', color: '#6b6b6b' }}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Day Detail Modal */}
      {showDayModal && selectedDay && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
          onClick={() => setShowDayModal(false)}
        >
          <div
            className="card"
            style={{
              maxWidth: '500px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
                paddingBottom: '1rem',
                borderBottom: '1px solid #e5e0db',
              }}
            >
              <div>
                <h3 style={{ marginBottom: '0.25rem' }}>
                  {format(selectedDay, 'EEEE, MMMM d, yyyy')}
                </h3>
                <p style={{ fontSize: '0.875rem', color: '#6b6b6b' }}>
                  {selectedDayAppointments.length} appointment
                  {selectedDayAppointments.length !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => setShowDayModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  color: '#6b6b6b',
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Appointments List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {selectedDayAppointments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b6b6b' }}>
                  <CalendarIcon size={48} style={{ margin: '0 auto 1rem' }} />
                  <p>No appointments scheduled for this day</p>
                </div>
              ) : (
                selectedDayAppointments.map((apt) => (
                  <div
                    key={apt.id}
                    style={{
                      padding: '1rem',
                      border: '1px solid #e5e0db',
                      borderRadius: '0.5rem',
                      borderLeft: `4px solid ${getStatusColor(apt.status)}`,
                      backgroundColor: '#F9FAFB',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '0.75rem',
                      }}
                    >
                      <div>
                        <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                          {getClientName(apt.clientId)}
                        </h4>
                        <span
                          style={{
                            fontSize: '0.75rem',
                            padding: '0.125rem 0.5rem',
                            backgroundColor: getStatusColor(apt.status),
                            color: 'white',
                            borderRadius: '0.25rem',
                            textTransform: 'capitalize',
                          }}
                        >
                          {apt.status.replace('-', ' ')}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div
                          style={{
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: '#4F6367',
                          }}
                        >
                          {format(apt.startTime, 'h:mm a')}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b6b6b' }}>
                          {apt.duration} min
                        </div>
                      </div>
                    </div>

                    <div style={{ fontSize: '0.875rem', color: '#6b6b6b' }}>
                      {(currentUser?.role === 'supervisor' || currentUser?.role === 'admin') && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginBottom: '0.5rem',
                          }}
                        >
                          <UserIcon size={14} />
                          <span>RBT: {getRbtName(apt.rbtId)}</span>
                        </div>
                      )}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginBottom: '0.5rem',
                        }}
                      >
                        <Clock size={14} />
                        <span>
                          {format(apt.startTime, 'h:mm a')} - {format(apt.endTime, 'h:mm a')}
                        </span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.5rem',
                        }}
                      >
                        <MapPin size={14} style={{ marginTop: '2px', flexShrink: 0 }} />
                        <span>
                          {apt.location.address}, {apt.location.city}, {apt.location.state}
                        </span>
                      </div>
                      {apt.cameraRequired && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginTop: '0.5rem',
                            color: '#10B981',
                          }}
                        >
                          <Video size={14} />
                          <span>Camera Required</span>
                        </div>
                      )}
                    </div>

                    {apt.notes && (
                      <div
                        style={{
                          marginTop: '0.75rem',
                          padding: '0.75rem',
                          backgroundColor: '#FFFFFF',
                          borderLeft: '3px solid #4F6367',
                          borderRadius: '0.25rem',
                          fontSize: '0.875rem',
                          color: '#374151',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: '#6b6b6b',
                            marginBottom: '0.25rem',
                            textTransform: 'uppercase',
                          }}
                        >
                          Notes
                        </div>
                        {apt.notes}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
